import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { Field, inputClass } from "../../components/forms/Field";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  CARD,
  Card,
  EmptyState,
  Notice,
  Panel,
  ROW_BTN,
  STALE,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import Skeleton from "../../components/Skeleton/Skeleton";
import RunStatusPanel, {
  RESUMABLE_STOPS,
  RUN_STATUS_CLASS,
  isRunInFlight,
  isRunReviewable,
} from "../../components/timetable/RunStatusPanel";
import CommitDialog from "../../components/timetable/CommitDialog";
import { apiErrorCode, apiErrorDetails, runErrorMessage } from "../../components/timetable/runErrors";
import {
  cancelRun,
  getRun,
  getRunEstimate,
  listRuns,
  startRun,
  stopRun,
} from "../../services/timetableGenerationService";
import { getAcademicTerms, getScheduleConstraints } from "../../services/schedulingService";
import { formatDateTime } from "../../utils/helpers/dateDisplay";

// How often we re-read a run that is still queued/running. The solver publishes
// `progress` on the run row roughly this often, so a faster poll would only
// re-read the same numbers; a slower one would make the progress panel lag.
const POLL_INTERVAL_MS = 2000;
// A poll that fails is usually a blip (token refresh, a dropped connection).
// Retry quietly a few times before telling the operator the watch broke.
const MAX_POLL_FAILURES = 3;

// carry_locks: where the locks held across this regeneration come from. Each
// option carries one line of copy because the difference is not guessable.
const LOCK_MODES = ["run", "grid", "none"];

const shortUuid = (uuid) => String(uuid || "").slice(0, 8);

const formatWhen = (value) => formatDateTime(value);

export default function GeneratePage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  // Reads are scoped to the whole org SUBTREE server-side, so a head-office
  // manager would otherwise auto-select a CHILD school's in-flight run and
  // start polling — and commit — someone else's board.
  const orgUuid = user?.organizations?.[0]?.organization?.uuid;

  const [runs, setRuns] = useState([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(true);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [run, setRun] = useState(null);
  const [isLoadingRun, setIsLoadingRun] = useState(false);

  const [terms, setTerms] = useState([]);
  const [termUuid, setTermUuid] = useState("");
  // Whether the term list has come back, however it came back. The estimate
  // waits on THIS rather than on `termUuid` being set: a school with no term
  // marked current never sets one, so keying on the uuid left `isEstimating`
  // true forever and the strip showed a loading skeleton that never resolved.
  const [termsLoaded, setTermsLoaded] = useState(false);
  const [carryLocks, setCarryLocks] = useState("run");
  // What this run would involve — the honest replacement for asking the
  // operator how many seconds to spend. Also carries the pre-flight findings,
  // which are pure arithmetic and so can be shown BEFORE the wait.
  const [estimate, setEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(true);

  const [isStarting, setIsStarting] = useState(false);
  const [conflictUuid, setConflictUuid] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showCommit, setShowCommit] = useState(false);
  const [constraintLabels, setConstraintLabels] = useState({});

  const loadRuns = useCallback(
    (signal) =>
      listRuns({ params: { organization: orgUuid, page_size: 10 }, signal })
        .then((data) => {
          const results = Array.isArray(data) ? data : data?.results || [];
          setRuns(results);
          return results;
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return [];
          toast.error(runErrorMessage(err, t, t("timetable.runsLoadFailed")));
          return [];
        })
        .finally(() => setIsLoadingRuns(false)),
    [t, orgUuid],
  );

  // Mount-once: the run history (so a reload lands back on the run in flight)
  // and the reference data the start form needs.
  useEffect(() => {
    const controller = new AbortController();

    loadRuns(controller.signal).then((results) => {
      if (controller.signal.aborted || results.length === 0) return;
      // Prefer whatever is still solving; otherwise the newest run (the list is
      // ordered -created_at server-side).
      const watching = results.find((r) => isRunInFlight(r.status)) || results[0];
      setSelectedUuid((current) => current || watching.uuid);
    });

    // The real term list, not the terms that happen to appear on page 1 of the
    // curriculum: a run targets ONE term, and offering only terms that already
    // have activities makes it impossible to generate for a term you are about
    // to author. Default to the term covering today.
    getAcademicTerms({ signal: controller.signal })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setTerms(list);
        const current = list.find((term) => term.is_current);
        if (current) setTermUuid((value) => value || current.uuid);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setTerms([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTermsLoaded(true);
      });

    return () => controller.abort();
  }, [loadRuns, orgUuid]);

  // The estimate is TERM-SPECIFIC and refetches when the term changes. The old
  // "is there anything to schedule" guard counted the school's activities
  // across every term at once, so a manager generating for a term they had not
  // authored yet got a green button and a run that "succeeded" with an empty
  // board — which, committed, deletes the school's whole timetable.
  useEffect(() => {
    // Waits for the term LIST. Firing on mount with `term: undefined` made the
    // server resolve the current term and build a whole solver Instance, then
    // do it again microseconds later when `getAcademicTerms` resolved and set
    // the very same term — two full builds for one page view. Waiting on the
    // list rather than on a resolved uuid keeps that saving while still asking
    // once when no term is marked current, which is exactly what the form's
    // default "Current term" option means anyway.
    if (!termsLoaded) return undefined;
    const controller = new AbortController();
    setIsEstimating(true);
    getRunEstimate({
      params: { term: termUuid || undefined },
      signal: controller.signal,
    })
      .then((data) => setEstimate(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setEstimate(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsEstimating(false);
      });
    return () => controller.abort();
  }, [termUuid, termsLoaded]);

  // Fetch the selected run, then keep polling it while it is in flight. The
  // recursive timeout (rather than setInterval) means a slow response can never
  // stack requests, and the cleanup kills both the timer and the in-flight
  // request — nothing survives unmount or a change of run.
  useEffect(() => {
    if (!selectedUuid) {
      setRun(null);
      return undefined;
    }
    const controller = new AbortController();
    let timer = null;
    let stopped = false;
    let failures = 0;
    let sawInFlight = false;

    setIsLoadingRun(true);
    setConstraintLabels({});

    const tick = () => {
      getRun({ uuid: selectedUuid, signal: controller.signal })
        .then((data) => {
          if (stopped) return;
          failures = 0;
          setRun(data);
          setIsLoadingRun(false);
          if (isRunInFlight(data.status)) {
            sawInFlight = true;
            timer = setTimeout(tick, POLL_INTERVAL_MS);
            return;
          }
          if (sawInFlight) {
            // It finished while we were watching: announce the outcome once and
            // refresh the history so the list status matches.
            sawInFlight = false;
            loadRuns();
            if (isRunReviewable(data.status)) toast.success(t("timetable.runFinished"));
            else toast.error(t(`timetable.status.${data.status}`, data.status));
          }
        })
        .catch((err) => {
          if (stopped || err?.code === "ERR_CANCELED") return;
          setIsLoadingRun(false);
          failures += 1;
          if (failures < MAX_POLL_FAILURES) {
            timer = setTimeout(tick, POLL_INTERVAL_MS * 2);
            return;
          }
          toast.error(runErrorMessage(err, t, t("timetable.runLoadFailed")));
        });
    };

    tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller.abort();
    };
  }, [selectedUuid, loadRuns, t]);

  // `core` names the authored rules that cannot hold together, by uuid. Resolve
  // those to their labels so the panel can say WHICH rules — only bothering when
  // a run actually blamed a rule (not a lock).
  const hasCoreRules = useMemo(
    () => (run?.diagnostics?.core || []).some((item) => item?.kind !== "lock"),
    [run?.diagnostics],
  );

  useEffect(() => {
    if (!hasCoreRules) return undefined;
    const controller = new AbortController();
    getScheduleConstraints({ params: { page_size: MAX_PAGE_SIZE }, signal: controller.signal })
      .then((data) => {
        const rows = Array.isArray(data) ? data : data?.results || [];
        setConstraintLabels(
          Object.fromEntries(rows.map((row) => [String(row.uuid), row.kind_label || row.kind])),
        );
      })
      .catch(() => {
        // Labels are a nicety; the panel falls back to the short ref.
      });
    return () => controller.abort();
  }, [hasCoreRules]);

  // `locks` is passed explicitly rather than read from state so "Keep looking"
  // can force carry_locks="run" in the same tick — a setState before the call
  // would not have landed by the time the body is built.
  const onStart = async (event, locks = carryLocks) => {
    event?.preventDefault?.();
    setIsStarting(true);
    setConflictUuid(null);
    try {
      // No time budget: the solver derives one from the instance and stops when
      // the week stops improving. There is nothing here for a manager to guess.
      const body = { carry_locks: locks };
      if (termUuid) body.term = termUuid;
      const created = await startRun({ body });
      setSelectedUuid(created.uuid);
      toast.success(t("timetable.runQueued"));
      loadRuns();
    } catch (err) {
      if (apiErrorCode(err) === "already_running") {
        // The school gets one generation at a time — send the operator to it
        // instead of leaving them pressing a button that cannot work.
        setConflictUuid(apiErrorDetails(err)[0] || null);
        loadRuns();
      } else {
        toast.error(runErrorMessage(err, t, t("timetable.startFailed")));
      }
    } finally {
      setIsStarting(false);
    }
  };

  const onCancel = async () => {
    if (!run) return;
    setIsCancelling(true);
    try {
      const data = await cancelRun({ uuid: run.uuid });
      // The cancel response has no placements — merge so the board summary stays.
      setRun((current) => ({ ...current, ...data }));
      loadRuns();
      toast.success(t("timetable.runCancelled"));
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.cancelFailed")));
    } finally {
      setIsCancelling(false);
    }
  };

  // The opposite of cancel: end the search but keep the board. The run stays in
  // flight until the worker notices, so the existing poll carries it to its
  // terminal state — nothing to do here but ask.
  const onStop = async () => {
    if (!run) return;
    setIsStopping(true);
    try {
      await stopRun({ uuid: run.uuid });
      toast.success(t("timetable.stopRequested"));
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.stopFailed")));
    } finally {
      setIsStopping(false);
    }
  };

  // "Keep looking" — the real answer to "was that good enough?". Carrying this
  // run's locks means everything the manager already pinned survives, so a
  // second pass is cheap rather than a restart from nothing.
  const onKeepLooking = () => {
    setCarryLocks("run");
    onStart(null, "run");
  };

  const onCommitted = (result) => {
    loadRuns();
    setRun((current) => (current && result?.run ? { ...current, ...result.run } : current));
    toast.success(t("timetable.commitSucceeded", { written: result?.written ?? 0 }));
  };

  const inFlight = isRunInFlight(run?.status);
  const reviewable = Boolean(run?.is_reviewable ?? isRunReviewable(run?.status));
  // A run with no lessons to schedule "succeeds" with an empty board, and
  // applying that board deletes the school's timetable and writes nothing
  // back. The backend refuses the commit; this stops the pointless run.
  // Read off the estimate, so it is scoped to the TERM being generated.
  const nothingToSchedule = estimate?.cards === 0;
  // The search ended with more still to find, so offer another pass rather
  // than leaving the manager to wonder whether this was the best week.
  const canKeepLooking =
    !inFlight && reviewable && RESUMABLE_STOPS.has(run?.stop_reason);

  // Page wrapper, back link and permission guard live on TimetableWorkspace.
  // The dark hero and its three pills went too: the pills WERE the workaround
  // for setup living on unrelated pages, and the tab strip is now that
  // navigation — keeping both would give the same four destinations twice.
  return (
    <>
      {/* CommitDialog stays a SIBLING of the panel, not a child: `ModalShell`
          is `fixed inset-0` rather than a portal, so putting it inside the
          panel's `space-y-*` stack would margin-shift the overlay. */}
      <Panel>
        <PanelHeader
          title={t("timetable.generateTitle")}
          subtitle={t("timetable.generateSubtitle")}
        />

        {nothingToSchedule && (
          <Notice
            title={t("timetable.noActivitiesTitle")}
            action={
              <Link to="/timetable/lessons" className={BTN.secondary}>
                {t("timetable.tab.lessons")}
              </Link>
            }
          >
            {t("timetable.noActivitiesHint")}
          </Notice>
        )}

        <form onSubmit={onStart} className={`${CARD} space-y-5 p-6`} noValidate>
          {/* What is about to be scheduled, stated as facts rather than as a
              field. This used to be a bordered, Field-labelled box sitting in
              the input grid beside Term, so it read as a control that could not
              be operated — and it quoted the solver's internal ceiling ("well
              inside 106 seconds"), a number that is neither a prediction nor
              anything a school can act on. */}
          <EstimateStrip estimate={estimate} isLoading={isEstimating} />

          {/* The two questions this form actually asks, side by side from `lg`.
              They were stacked, with Term capped at `max-w-sm` — which read as
              a full-width row containing one small control, and pushed the
              submit button further down the page than the form is tall. */}
          <div className="grid gap-5 xl:grid-cols-[16rem_minmax(0,1fr)]">
            <Field label={t("timetable.term")}>
              <select
                value={termUuid}
                disabled={isStarting}
                onChange={(e) => setTermUuid(e.target.value)}
                className={inputClass(false)}
              >
                <option value="">{t("timetable.termCurrent")}</option>
                {terms.map((term) => (
                  <option key={term.uuid} value={term.uuid}>
                    {term.name}
                  </option>
                ))}
              </select>
            </Field>

            <fieldset className="min-w-0">
              <legend className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {t("timetable.carryLocks")}
              </legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {LOCK_MODES.map((mode) => (
                  <label
                    key={mode}
                    className={`cursor-pointer rounded-2xl border p-3 transition ${
                      carryLocks === mode
                        ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20"
                        : "border-slate-200 bg-white hover:border-indigo-300"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="carry_locks"
                        value={mode}
                        checked={carryLocks === mode}
                        disabled={isStarting}
                        onChange={() => setCarryLocks(mode)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-xs font-black text-slate-800">
                        {t(`timetable.locks.${mode}`)}
                      </span>
                    </span>
                    <span className="mt-1.5 block text-[11px] font-medium leading-snug text-slate-500">
                      {t(`timetable.locksHint.${mode}`)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Pure arithmetic ("this class needs 44 periods but the week holds
              40"), so there is no reason to make anyone wait for a solve to be
              told it. It used to run only AFTER a failed run. */}
          {estimate?.findings?.length > 0 && (
            <Notice title={t("timetable.preflightTitle")}>
              <p>{t("timetable.preflightHint")}</p>
              <ul className="mt-2.5 space-y-1.5">
                {estimate.findings.map((finding, index) => (
                  <li
                    key={`${finding.code || "finding"}-${index}`}
                    className="flex gap-2 font-bold leading-relaxed"
                  >
                    <span aria-hidden="true" className="shrink-0 text-amber-400">•</span>
                    <span>{finding.message || finding.code}</span>
                  </li>
                ))}
              </ul>
            </Notice>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <p className="max-w-md text-[11px] font-medium leading-relaxed text-slate-500">
              {t("timetable.generateHint")}{" "}
              {/* No seconds. "Under a minute" is what a manager can plan
                  around; the exact ceiling is an implementation detail that
                  changes with the size of the school. */}
              <span className="text-slate-600">{t("timetable.generateTiming")}</span>
            </p>
            {/* Held while the estimate is in flight: `nothingToSchedule` is
                read off it, so between changing the term and the numbers
                arriving the guard is answering for the PREVIOUS term — long
                enough to start a run against an empty one, which succeeds with
                an empty board and offers to wipe the school's timetable. */}
            <button
              type="submit"
              disabled={isStarting || inFlight || nothingToSchedule || isEstimating}
              title={nothingToSchedule ? t("timetable.noActivitiesTitle") : undefined}
              className={BTN.primary}
            >
              {isStarting ? t("timetable.starting") : t("timetable.startRun")}
            </button>
          </div>
        </form>

        {conflictUuid !== null && (
          <Notice
            role="alert"
            title={t("timetable.errAlreadyRunning")}
            action={
              conflictUuid && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUuid(conflictUuid);
                    setConflictUuid(null);
                  }}
                  className={BTN.secondary}
                >
                  {t("timetable.watchRun")}
                </button>
              )
            }
          >
            {t("timetable.alreadyRunningHint")}
          </Notice>
        )}

        {isLoadingRun && !run && (
          <div aria-busy="true">
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        )}

        {run && (
          <div className="space-y-3">
            <RunStatusPanel run={run} constraintLabels={constraintLabels} />
            <div className="flex flex-wrap gap-2 justify-end">
              {inFlight && (
                <>
                  {/* The two are one word apart and mean opposite things, so
                      they are labelled by their OUTCOME rather than their verb:
                      one keeps the board, the other throws it away. "Stop and
                      use this board" is the control that replaced the seconds
                      field — it asks "is this good enough?", which a human can
                      answer while looking at a week, instead of "how many
                      seconds?", which nobody can answer in advance. */}
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isCancelling || isStopping}
                    className={BTN.secondary}
                  >
                    {isCancelling ? t("timetable.cancelling") : t("timetable.cancelRun")}
                  </button>
                  <button
                    type="button"
                    onClick={onStop}
                    disabled={isStopping || isCancelling}
                    className={BTN.accent}
                  >
                    {isStopping ? t("timetable.stopping") : t("timetable.stopAndKeep")}
                  </button>
                </>
              )}
              {canKeepLooking && (
                <button
                  type="button"
                  onClick={onKeepLooking}
                  disabled={isStarting}
                  className={BTN.accent}
                >
                  {t("timetable.keepLooking")}
                </button>
              )}
              {reviewable && (
                <>
                  <Link
                    to={`/timetable/draft/${run.uuid}`}
                    className={BTN.secondary}
                  >
                    {t("timetable.openBoard")}
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowCommit(true)}
                    className={BTN.danger}
                  >
                    {t("timetable.commitConfirm")}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <RunHistory
          runs={runs}
          isLoading={isLoadingRuns}
          selectedUuid={selectedUuid}
          onSelect={setSelectedUuid}
        />
      </Panel>

      {showCommit && run && (
        <CommitDialog
          runUuid={run.uuid}
          onClose={() => setShowCommit(false)}
          onCommitted={onCommitted}
        />
      )}
    </>
  );
}

// What is about to be scheduled, as three numbers.
//
// This replaced a bordered box captioned "What this will involve" that sat in
// the form's input grid — so it looked like a field you could not fill in — and
// whose body quoted the solver's internal wall-clock ceiling. That number is a
// backstop, not an estimate: it scales with the size of the school, it is
// almost never reached, and "well inside 106 seconds" reads as a promise about
// something the reader never asked about. The counts are the part that was
// actually useful, so that is all this keeps.
function EstimateStrip({ estimate, isLoading }) {
  const { t } = useTranslation();

  if (isLoading && !estimate) {
    return <Skeleton className="h-14 rounded-2xl" />;
  }
  if (!estimate || !estimate.cards) return null;

  const stats = [
    { value: estimate.cards, label: t("timetable.estimateLessons") },
    { value: estimate.classes, label: t("timetable.estimateClasses") },
    { value: estimate.teachers, label: t("timetable.estimateTeachers") },
  ];

  return (
    // Changing the term refetches these three numbers, and until they arrive
    // they describe the term you were looking at a second ago — a manager
    // reading "840 lessons" against the term they have just switched to is the
    // one thing this strip must not do.
    <div
      aria-busy={isLoading}
      className={`flex flex-wrap items-center gap-x-8 gap-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-3 ${
        isLoading ? STALE : ""
      }`}
    >
      {stats.map((stat) => (
        <div key={stat.label}>
          <p className="text-xl font-black text-slate-900 tabular-nums leading-none">
            {stat.value}
          </p>
          <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function RunHistory({ runs, isLoading, selectedUuid, onSelect }) {
  const { t } = useTranslation();

  if (isLoading) {
    // Shaped like the runs table it becomes — four columns, two of them hidden
    // below `md`, exactly as the real one hides them.
    return (
      <TableSkeleton
        rows={3}
        columns={["w-16", "w-20", "w-32 hidden md:block", "w-24 hidden md:block"]}
      />
    );
  }

  if (runs.length === 0) {
    return (
      <EmptyState
        iconPath="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z"
        title={t("timetable.noRuns")}
        hint={t("timetable.noRunsHint")}
      />
    );
  }

  return (
    <Card padded={false}>
      <p className="border-b border-slate-100 px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">
        {t("timetable.recentRuns")}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className={TABLE_HEAD}>
            <tr>
              <th className="px-6 py-3">{t("timetable.colRun")}</th>
              <th className="px-6 py-3">{t("timetable.colStatus")}</th>
              <th className="px-6 py-3 hidden md:table-cell">{t("timetable.colStarted")}</th>
              <th className="px-6 py-3 hidden md:table-cell">{t("timetable.colBy")}</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {runs.map((row) => (
              <tr
                key={row.uuid}
                className={`border-t border-slate-100 ${
                  row.uuid === selectedUuid ? "bg-indigo-50/60" : "hover:bg-slate-50"
                }`}
              >
                <td className="px-6 py-3 font-mono font-black text-slate-500">{shortUuid(row.uuid)}</td>
                <td className="px-6 py-3">
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest ${
                      RUN_STATUS_CLASS[row.status] || "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t(`timetable.status.${row.status}`, row.status)}
                  </span>
                </td>
                <td className="px-6 py-3 text-slate-600 hidden md:table-cell">
                  {formatWhen(row.started_at || row.created_at)}
                </td>
                <td className="px-6 py-3 text-slate-600 hidden md:table-cell">{row.created_by || "—"}</td>
                <td className="px-6 py-3 text-right">
                  {row.uuid === selectedUuid ? (
                    <span className="px-2 py-1 text-xs font-semibold text-indigo-400">
                      {t("timetable.watching")}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelect(row.uuid)}
                      className={ROW_BTN.edit}
                    >
                      {t("timetable.viewRun")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
